import { api } from '@setplex/tria-api'
import {
  attach,
  combine,
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector'
import { createForm } from 'effector-forms'
import { debounce } from 'patronum/debounce'
import { model as i18n } from '~/core/i18n'
import { model as router } from '~/core/router'
import { signInByTrueIdFeatureModel } from '~/features/subscriber/sign-in'
import { aye, nay } from '~/shared/helpers'
import { actualUserInput } from '~/shared/html-events/input'
import { logger } from '~/shared/logger'
import { email, required } from '~/shared/validators'
import { token } from '../../shared/tools'

// init page
export const init = createEvent()

// navigate to /sing-in if trueId authentication allowed
sample({
  clock: init,
  source: signInByTrueIdFeatureModel.$trueIdConfig,
  filter: (trueIdConfig) => aye(trueIdConfig),
  fn: () => '/sign-in',
  target: router.navigateReplace,
})

// reset password request effect
export const initResetPasswordFx = attach({
  effect: api.subscriber.initResetPasswordFx,
})

// effect to create hash token
const tokenFx = createEffect<
  { id: string; time: number; unit: string },
  string
>(({ id, time, unit }) => token(id, time, unit))

// error to show to the user
export const $error = createStore<string | null>(null)

// debounced error, in order to prevent react to render component with old data
export const $viewError = createStore<string | null>(null)

// Transformed error state into a displayable message with translation
export const $message = combine($viewError, i18n.$t, (error, { t }) => ({
  show: Boolean(error),
  title: error ? t(error) : undefined,
}))

// forgot password form
export const forgotPasswordForm = createForm({
  fields: {
    email: {
      init: '' as string,
      rules: [required(), email()],
    },
  },
  validateOn: ['submit'],
})

const userUpdatesForm = actualUserInput(forgotPasswordForm.$values.updates)

// send reset password request
sample({
  clock: forgotPasswordForm.formValidated,
  fn: ({ email }) => ({ email: email.trim() }),
  target: initResetPasswordFx,
})

// reset form in case we came here not from /forgot-password subpages
sample({
  clock: init,
  source: router.$previous,
  filter: (location) => nay(location?.pathname.startsWith('/forgot-password')),
  target: forgotPasswordForm.reset,
})

// create hash token from reset password response
sample({
  clock: initResetPasswordFx.doneData,
  fn: ({ id, time_value, time_period }) => ({
    id,
    time: time_value,
    unit: time_period,
  }),
  target: tokenFx,
})

// in case we could not encode token -> log error
sample({
  clock: tokenFx.fail,
  fn: (fail) => ['could not encode token:', fail],
  target: logger.errorFx,
})

// redirect to verify page
sample({
  clock: tokenFx.doneData,
  source: initResetPasswordFx.done,
  fn: ({ params }, token) => ({
    pathname: '/forgot-password/verify',
    hash: token,
    state: params,
  }),
  target: router.navigatePush,
})

//
// handle dynamic fields errors (~ server side validation)
//

// WG0024 - Email is not valid
sample({
  clock: api.events.logic.emailNotValid,
  fn: () => ({ rule: 'invalid' }),
  target: forgotPasswordForm.fields.email.addError,
})

//
// add listeners for server errors
//

$error
  .reset(
    userUpdatesForm,
    init,

    // "server validation" errors
    api.events.logic.emailNotValid,
    api.events.logic.accountIpACLLock // handled by geoblock navigation sub-process
  )
  .on(
    api.events.logic.yourIPAddressLocked, // WG0018 - IP address is locked (limit exceeded)
    () => 'ip_blocked_error'
  )
  .on(
    api.events.logic.passwordResetDisabled, // WG0068 - Password reset is disabled
    () => 'reset_password_unavailable_error'
  )
  .on(
    api.events.logic.passwordResetBlockedForAccount, // WG0069 - Password reset is not allowed for account
    () => 'reset_password_unavailable_error'
  )
  .on(
    api.events.logic.noMoreRequestsForIPAllowed, // WG0071 - No more requests from this IP are allowed
    () => 'ip_blocked_error'
  )
  .on(
    api.events.logic.noMoreRequestsForEmailAllowed, // WG0072 - No more requests for this email are allowed
    () => 'email_no_more_request_allowed_error'
  )

  // some generic request errors
  .on(
    api.events.http.httpError, // any non-200 error
    (error) => (error === null ? 'unknown_error' : error)
  )
  .on(
    api.events.http.error, // any request error (dns, carrier, etc.)
    (error) => (error === null ? 'request_failed_error' : error)
  )

debounce({
  source: $error,
  timeout: 50,
  target: $viewError,
})

$viewError.reset(userUpdatesForm, init)
